/**
 * Theo dõi lượt bói chỉ tay chạy nền ở MỌI trang, và báo bằng toast khi xong.
 *
 * Mount một lần ở `__root` — không vẽ gì cả.
 *
 * Vì sao không để trang chỉ tay tự lo: từ v92 người xem được mời rời trang trong lúc
 * chờ 4–5 phút, mà rời trang thì `PalmScanStep` unmount và không còn ai poll. Push về
 * device chỉ tới khi họ đã rời hẳn app và đã cấp quyền thông báo — người đang mở app
 * ở tab Tử Vi thì không nhận được gì.
 *
 * Toast dùng đúng khuôn của thông báo điểm danh (`CheckinButton`): tiêu đề + mô tả +
 * một nút hành động đi thẳng tới nơi cần tới.
 */

import { useEffect, useState } from "react";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import { toast } from "sonner";

import { usePalmJob } from "@/hooks/use-palm-job";
import { useBoideUser } from "@/hooks/use-boide-user";
import {
  forgetPalmJob,
  PALM_JOB_EVENT,
  readPalmJob,
  type WatchedPalmJob,
} from "@/lib/palm-job-watch";

export function PalmJobWatcher() {
  const { user } = useBoideUser();
  const navigate = useNavigate();
  const [watched, setWatched] = useState<WatchedPalmJob | null>(readPalmJob);

  // `PALM_JOB_EVENT` cho tab hiện tại (sự kiện `storage` của trình duyệt cố ý KHÔNG
  // bắn cho chính tab đã ghi), `storage` cho các tab còn lại.
  useEffect(() => {
    const sync = () => setWatched(readPalmJob());
    window.addEventListener(PALM_JOB_EVENT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(PALM_JOB_EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  /**
   * Đang đứng ngay trên trang chỉ tay thì KHÔNG toast: trang ấy tự chuyển sang bảng
   * dấu hiệu / bài luận, và một toast nói "đã xong" chồng lên đúng thứ vừa hiện ra là
   * thừa. Vẫn xoá sổ theo dõi, vì việc đã kết thúc thật.
   */
  const onPalmPage = useRouterState({
    select: (s) => s.location.pathname.startsWith("/boi-chi-tay"),
  });

  // Cùng `queryKey` với `PalmScanStep` — gồm cả giai đoạn, nên lúc đang ở trang chỉ tay
  // và hai bên chờ cùng một giai đoạn thì react-query gộp làm một request. Sổ theo dõi
  // ghi sẵn giai đoạn lúc giao việc, nên ở đây không phải đoán.
  const job = usePalmJob(
    watched?.draftId ?? null,
    Boolean(user && watched),
    watched?.stage ?? "scan",
  );
  const state = job.data?.state;

  useEffect(() => {
    if (!watched || !state || state === "running") return;

    const draftId = watched.draftId;
    forgetPalmJob();
    setWatched(null);
    if (onPalmPage) return;

    if (state === "failed") {
      toast.error("Lượt xem chỉ tay chưa xong được", {
        description: job.data?.error || "Bấm để mở lại và chạy tiếp — không mất thêm phí.",
        action: {
          label: "Mở lại",
          onClick: () => navigate({ to: "/boi-chi-tay", search: { draft: draftId } }),
        },
        duration: 12000,
      });
      return;
    }

    // Hai giai đoạn dẫn tới hai chỗ khác nhau: quét xong thì việc kế tiếp của người
    // xem là SOÁT bảng dấu hiệu, còn luận xong mới là mở bài ra đọc.
    const toScan = job.data?.stage === "scan";
    toast.success(toScan ? "Đã đọc xong bàn tay" : "Bài luận tướng tay đã xong", {
      description: toScan
        ? "Vào soát lại danh sách dấu hiệu rồi bấm luận giải nhé."
        : "Thầy đã luận xong. Bấm để đọc bài.",
      action: {
        label: toScan ? "Soát dấu hiệu" : "Đọc bài luận",
        onClick: () =>
          navigate({
            to: "/boi-chi-tay",
            search: toScan ? { draft: draftId } : { reading: draftId },
          }),
      },
      duration: 15000,
    });
    // `job.data` cố ý không nằm trong deps: nó là object mới sau mỗi nhịp poll, và
    // effect này chỉ được chạy khi TRẠNG THÁI đổi — không thì mỗi nhịp một toast.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, watched, onPalmPage]);

  return null;
}
